/**
 * useSubunits — React Query hooks for subunit CRUD
 * Pattern: matches useObstacles.ts
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchSubunits,
  fetchSubunitsByLocation,
  createSubunit,
  updateSubunit,
  deleteSubunit,
} from '@/lib/api/subunits'
import {
  mapUnitRowToSubunit,
  type Subunit,
  type CreateSubunitInput,
  type UpdateSubunitInput,
} from '@/types/subunit'
import { useTenant } from '@/contexts'
import { toast } from 'sonner'

/**
 * Query key factory for subunits
 */
export const subunitKeys = {
  all: ['subunits'] as const,
  lists: () => [...subunitKeys.all, 'list'] as const,
  byParent: (parentUnitId: string) => [...subunitKeys.lists(), 'parent', parentUnitId] as const,
  byLocation: (locationId: string) => [...subunitKeys.lists(), 'location', locationId] as const,
}

/**
 * Fetch subunits for a specific parent unit
 */
export function useSubunits(parentUnitId: string | undefined) {
  return useQuery({
    queryKey: subunitKeys.byParent(parentUnitId ?? ''),
    queryFn: async () => {
      const rows = await fetchSubunits(parentUnitId!)
      return rows.map(mapUnitRowToSubunit)
    },
    enabled: !!parentUnitId,
  })
}

/**
 * Fetch all subunits for a location (all buildings)
 */
export function useSubunitsByLocation(locationId: string | undefined) {
  return useQuery({
    queryKey: subunitKeys.byLocation(locationId ?? ''),
    queryFn: async () => {
      const rows = await fetchSubunitsByLocation(locationId!)
      return rows.map(mapUnitRowToSubunit)
    },
    enabled: !!locationId,
  })
}

/**
 * Subunit CRUD mutations
 */
export function useSubunitMutations() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: subunitKeys.lists() })
  }

  const create = useMutation({
    mutationFn: async (input: CreateSubunitInput) => {
      if (!currentOrganization?.id) throw new Error('No organization selected')

      const insert: Record<string, unknown> = {
        organization_id: currentOrganization.id,
        location_id: input.locationId,
        area_id: input.areaId,
        parent_unit_id: input.parentUnitId,
        name: input.name,
        unit_code: input.unitCode,
        type: input.category,
        interior_width: input.width,
        depth: input.depth,
        interior_height: input.height,
        offset_x: input.offsetX,
        offset_y: input.offsetY,
        elevation_ft: input.elevationFt ?? 0,
        clearance_buffer_ft: input.clearanceBufferFt ?? 1.5,
        deduct_from_parent: input.deductFromParent ?? true,
        status: 'available',
        sequence: Math.floor(Date.now() / 1000) % 100000,
      }

      const row = await createSubunit(insert)
      return mapUnitRowToSubunit(row)
    },
    onSuccess: () => {
      invalidate()
      toast.success('Subunit created')
    },
    onError: (error: Error) => {
      toast.error('Failed to create subunit', { description: error.message })
    },
  })

  const update = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateSubunitInput }) => {
      const dbUpdate: Record<string, unknown> = {}

      if (data.name !== undefined) dbUpdate.name = data.name
      if (data.unitCode !== undefined) dbUpdate.unit_code = data.unitCode
      if (data.category !== undefined) dbUpdate.type = data.category
      if (data.width !== undefined) dbUpdate.interior_width = data.width
      if (data.depth !== undefined) dbUpdate.depth = data.depth
      if (data.height !== undefined) dbUpdate.interior_height = data.height
      if (data.offsetX !== undefined) dbUpdate.offset_x = data.offsetX
      if (data.offsetY !== undefined) dbUpdate.offset_y = data.offsetY
      if (data.elevationFt !== undefined) dbUpdate.elevation_ft = data.elevationFt
      if (data.clearanceBufferFt !== undefined) dbUpdate.clearance_buffer_ft = data.clearanceBufferFt
      if (data.deductFromParent !== undefined) dbUpdate.deduct_from_parent = data.deductFromParent
      if (data.notes !== undefined) dbUpdate.notes = data.notes
      if (data.status !== undefined) dbUpdate.status = data.status

      const row = await updateSubunit(id, dbUpdate)
      return mapUnitRowToSubunit(row)
    },
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: subunitKeys.lists() })

      const previousQueries = queryClient.getQueriesData<Subunit[]>({
        queryKey: subunitKeys.lists(),
      })

      for (const [queryKey] of previousQueries) {
        queryClient.setQueryData<Subunit[]>(queryKey, (old) =>
          old?.map((s) => (s.id === id ? { ...s, ...data } : s))
        )
      }

      return { previousQueries }
    },
    onError: (error: Error, _vars, context) => {
      if (context?.previousQueries) {
        for (const [queryKey, prev] of context.previousQueries) {
          queryClient.setQueryData(queryKey, prev)
        }
      }
      toast.error('Failed to update subunit', { description: error.message })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: subunitKeys.lists() })
    },
  })

  const remove = useMutation({
    mutationFn: async (id: string) => {
      await deleteSubunit(id)
    },
    onSuccess: () => {
      invalidate()
      toast.success('Subunit deleted')
    },
    onError: (error: Error) => {
      toast.error('Failed to delete subunit', { description: error.message })
    },
  })

  const duplicate = useMutation({
    mutationFn: async (subunit: Subunit) => {
      if (!currentOrganization?.id) throw new Error('No organization selected')

      const insert: Record<string, unknown> = {
        organization_id: currentOrganization.id,
        location_id: subunit.locationId,
        area_id: subunit.areaId,
        parent_unit_id: subunit.parentUnitId,
        name: `${subunit.name} (copy)`,
        unit_code: `${subunit.unitCode}-copy`,
        type: subunit.category,
        interior_width: subunit.width,
        depth: subunit.depth,
        interior_height: subunit.height,
        offset_x: subunit.offsetX + 2,
        offset_y: subunit.offsetY + 2,
        elevation_ft: subunit.elevationFt,
        clearance_buffer_ft: subunit.clearanceBufferFt,
        deduct_from_parent: subunit.deductFromParent,
        status: 'available',
        sequence: Math.floor(Date.now() / 1000) % 100000,
      }

      const row = await createSubunit(insert)
      return mapUnitRowToSubunit(row)
    },
    onSuccess: () => {
      invalidate()
      toast.success('Subunit duplicated')
    },
    onError: (error: Error) => {
      toast.error('Failed to duplicate subunit', { description: error.message })
    },
  })

  return { create, update, remove, duplicate }
}
